import { useEffect, useState } from "react";
import { DEFAULT_PAGE_SIZE } from "../../configs/constants";
import Preference from "../UI/Preference";
import Pagination from "../UI/Pagination";
import { getHeaderCounterText, getPagedItems } from "../../utils/common";
import {
  formatDate,
  formatPercentage,
  change3Digits,
} from "../../utils/format";
import {
  Table as CSDTable,
  Button,
  Box,
  Header,
  SpaceBetween,
} from "@cloudscape-design/components-themed";
import { Link, useNavigate, useParams } from "react-router-dom";
import { COMMON_LABELS } from "../default-constants";
import { LABELS } from "./constants";
import { useGetRateAdjustmentCaseMerchantContentsQuery } from "../../features/api";

const columnDefinitions = [
  {
    id: "merchantBranch",
    header: LABELS.BRANCH,
    cell: (e: any) => (
      <Link
        to={
          "/rate-adjustment-case/contents/" +
          e.rateAdjustmentCaseNo +
          "/" +
          e.merchantBranchNo +
          "/" +
          e.adjustmentBranchNo
        }
      >
        {e.adjustmentBranchNo}
      </Link>
    ),
  },
  {
    id: "contractRouteName",
    header: LABELS.CONTRACT_ROUTE_NAME,
    cell: (e: any) => (e.contractRoute === 1 ? "直接" : "包括"),
  },
  {
    id: "generalCompanyName",
    header: LABELS.GENERAL_COMPANY_NAME,
    cell: (e: any) =>
      e.contractRouteName !== "直接" ? e.contractRouteName : "",
  },
  {
    id: "settlementGwCompanyName",
    header: LABELS.SETTLEMENT_GW_COMPANY_NAME,
    cell: (e: any) => e.settlementGwCompanyName,
  },
  {
    id: "applicationDate",
    header: LABELS.APPLICATION_DATE,
    cell: (e: any) => formatDate(e.applicationDate),
  },
  {
    id: "responseDate",
    header: LABELS.RESPONSE_DATE,
    cell: (e: any) => formatDate(e.responseDate),
  },
  {
    id: "responseRate",
    header: LABELS.RESPONSE_RATE,
    cell: (e: any) => formatPercentage(e.responseRate),
  },
  {
    id: "desiredRate",
    header: LABELS.DESIRED_RATE,
    cell: (e: any) => e.desiredRate,
  },
  {
    id: "sales",
    header: LABELS.SALES,
    cell: (e: any) => change3Digits(e.sales),
  },
  {
    id: "allShopsNumber",
    header: LABELS.ALL_SHOP_NUMBER,
    cell: (e: any) => e.allShopsNumber,
  },
  {
    id: "installedShopsNumber",
    header: LABELS.INSTALLED_SHOP_NUMBER,
    cell: (e: any) => e.installedShopsNumber,
  },
  {
    id: "docomoIndustry",
    header: LABELS.DOCOMO_INDUSTRY,
    cell: (e: any) => e.docomoIndustry,
  },
  {
    id: "applicationIndustry",
    header: LABELS.APPLICATION_INDUSTRY,
    cell: (e: any) => e.applicationIndustry,
  },
  {
    id: "handlingProducts",
    header: LABELS.HANDLING_PRODUCTS,
    cell: (e: any) => e.handlingProducts,
  },
  {
    id: "ratePresentationCriteria",
    header: LABELS.RATE_PRESENTATION_CRITERIA,
    cell: (e: any) => e.ratePresentationCriteria,
  },
  {
    id: "costBurden",
    header: LABELS.COST_BURDEN,
    cell: (e: any) => e.costBurden,
  },
  {
    id: "costBurdenAdjustmentInformation",
    header: LABELS.COST_BURDEN_ADJUSTMENT_INFORMATION,
    cell: (e: any) => e.costBurdenAdjustmentInformation,
  },
  {
    id: "note",
    header: LABELS.NOTE,
    cell: (e: any) => e.note,
  },
];

/**
 * 料率調整案件一覧
 */
export const Table = (props: any) => {
  const navigate = useNavigate();

  const { rateAdjustmentCaseNo, merchantBranchNo } = useParams();

  //一覧データの取得
  const { data, isFetching, refetch } =
    useGetRateAdjustmentCaseMerchantContentsQuery({
      rateAdjustmentCaseNo: rateAdjustmentCaseNo as string,
      merchantBranchNo: merchantBranchNo as string,
      merchantName: "",
      contractServiceCode: "",
    });

  /**ページャ関連 */
  const options = columnDefinitions.map((column) => ({
    id: column.id,
    label: column.header,
  }));
  const [currentPageIndex, setCurrentPageIndex] = useState(1);
  const [preferences, setPreferences] = useState({
    pageSize: DEFAULT_PAGE_SIZE,
    visibleContent: options.map((o) => o.id),
  });

  const [pagedItems, setPagedItems] = useState<Object[]>([]);
  const [allItems, setAllItems] = useState<Object[]>([]);
  useEffect(() => {
    if (data !== undefined) {
      setPagedItems(getPagedItems(data, 1, DEFAULT_PAGE_SIZE));
      setAllItems(data);
      setCurrentPageIndex(1);
    }
  }, [data]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  /** 設定変更関数 */
  const handleOnConfirm = (detail: any) => {
    const newPageSize = detail.pageSize;
    setPreferences({
      pageSize: newPageSize,
      visibleContent: detail.visibleContent,
    });
    if (data !== undefined) {
      setPagedItems(data.slice(0, newPageSize));
      setCurrentPageIndex(1);
    }
  };

  return (
    <CSDTable
      columnDefinitions={columnDefinitions}
      loading={isFetching}
      loadingText={COMMON_LABELS.CSD.LOADING_TEXT}
      items={pagedItems}
      resizableColumns
      stickyHeader
      header={
        <SpaceBetween size="xs">
          <SpaceBetween direction="horizontal" size="m"></SpaceBetween>
          <Button
            onClick={() => {
              navigate(
                `/rate-adjustment-case/contents/${rateAdjustmentCaseNo}/${merchantBranchNo}`
              );
            }}
          >
            {LABELS.ADD_ADJUSTMENT_DETAILS}
          </Button>
          <Header>
            {LABELS.TITLE_TABLE}
            {getHeaderCounterText(allItems.length, pagedItems.length)}
          </Header>
        </SpaceBetween>
      }
      pagination={
        <Pagination
          currentPageIndex={currentPageIndex}
          filteredItems={allItems}
          setCurrentPageIndex={setCurrentPageIndex}
          pageSize={preferences.pageSize}
          setPagedItems={setPagedItems}
        />
      }
      preferences={
        <Preference
          preferences={preferences}
          options={options}
          handleOnConfirm={handleOnConfirm}
        />
      }
      empty={
        <Box textAlign="center" color="inherit">
          {COMMON_LABELS.CSD.NO_DATA}
        </Box>
      }
    />
  );
};
